import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCircleCheck, faEdit } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { Card } from '@sorare/core/src/components/card/Card';
import { LineupCardsLayout } from '@sorare/core/src/components/lineup/LineupCardsLayout';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { range } from '@sorare/core/src/lib/arrays';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary } from '@sorare/core/src/lib/glossary';
import { PlayerDrawer } from '@sorare/marketplace/src/components/player/PlayerDrawer';

import { EditFirstLineupDialog } from './EditFirstLineupDialog/index.tsx';
import { FirstLineupPlayerPrice } from './FirstLineupPlayerPrice/index.tsx';
import type {
  FirstLineupPreview_anyCard,
  FirstLineupPreview_so5Leaderboard,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 0 })`
  border-radius: var(--double-unit);
  background: linear-gradient(262deg, #f1ff98 5.3%, #87f6ab 91.08%), transparent;
`;
const FirstLineupPreviewHeader = styled(Horizontal)`
  padding: var(--unit);
`;
const FirstLineupPreviewContent = styled(Vertical).attrs({ gap: 2 })`
  border-radius: calc(var(--double-unit) - 1px);
  background:
    no-repeat center / cover var(--bg-url),
    var(--c-black);
  padding: var(--double-unit) var(--double-unit) 0 var(--double-unit);
`;
const RightAlignedHorizontal = styled(Horizontal)`
  justify-content: flex-end;
`;
const Footer = styled(Vertical).attrs({ center: true })`
  padding: var(--double-unit) 0;
`;
const SkeletonContent = styled.div`
  aspect-ratio: 100 / 160;
  width: 100%;
`;

type Props = {
  so5Leaderboard?: FirstLineupPreview_so5Leaderboard;
  cards: FirstLineupPreview_anyCard[];
  backgroundUrl?: string | null;
  onCartUpdated: () => Promise<void>;
  cartQueryLoading?: boolean;
};
export const FirstLineupPreview = ({
  so5Leaderboard,
  cards,
  backgroundUrl,
  onCartUpdated,
  cartQueryLoading,
}: Props) => {
  const track = useEvents();
  const { toMonetaryAmount } = useMonetaryAmount();
  const [editDialogOpened, setEditDialogOpened] = useState(false);
  const [playerDetailsCard, setPlayerDetailsCard] =
    useState<FirstLineupPreview_anyCard | null>(null);

  return (
    <>
      <Root>
        <FirstLineupPreviewHeader center>
          <FontAwesomeIcon icon={faCircleCheck} color="var(--c-black)" />
          <BrandS color="var(--c-black)" className="text-center">
            <FormattedMessage
              id="RecommendedPlayers.discountApplied2"
              defaultMessage="Welcome discount applied"
            />
          </BrandS>
        </FirstLineupPreviewHeader>
        <FirstLineupPreviewContent
          style={{ '--bg-url': `url(${backgroundUrl || ''})` } as CSSProperties}
        >
          <RightAlignedHorizontal>
            <Button
              color="secondary"
              size="medium"
              startIcon={<FontAwesomeIcon icon={faEdit} />}
              onClick={() => {
                track('Click Edit Team In Guidance First Lineup', {});
                setEditDialogOpened(true);
              }}
            >
              <FormattedMessage {...glossary.edit} />
            </Button>
          </RightAlignedHorizontal>
          <LineupCardsLayout fillHeightPercent={0.6}>
            {cards
              ? cards.map(card => (
                  <button
                    key={card?.slug}
                    type="button"
                    onClick={() => {
                      track(
                        'Check Player Details In Guidance First Lineup',
                        {}
                      );
                      setPlayerDetailsCard(card);
                    }}
                  >
                    <Vertical center>
                      <Card card={card} />
                      {!!card.livePrimaryOffer?.price && (
                        <FirstLineupPlayerPrice
                          price={toMonetaryAmount(card.livePrimaryOffer.price)}
                        />
                      )}
                    </Vertical>
                  </button>
                ))
              : range(5).map(i => (
                  <Skeleton key={i} loading>
                    <SkeletonContent />
                  </Skeleton>
                ))}
          </LineupCardsLayout>
          <Footer>
            <LabelM>
              <FormattedMessage
                id="RecommendedPlayers.tapPlayerForInfo"
                defaultMessage="Tap a player to learn more"
              />
            </LabelM>
          </Footer>
        </FirstLineupPreviewContent>
      </Root>
      <PlayerDrawer
        cardOpened={
          playerDetailsCard
            ? {
                playerSlug: playerDetailsCard.anyPlayer.slug,
                cardSlug: playerDetailsCard.slug,
              }
            : null
        }
        onClose={() => setPlayerDetailsCard(null)}
        onboarding
      />
      {!!editDialogOpened && (
        <EditFirstLineupDialog
          open
          onClose={() => {
            setEditDialogOpened(false);
          }}
          backgroundUrl={backgroundUrl}
          so5Leaderboard={so5Leaderboard}
          cards={cards}
          onCartUpdated={onCartUpdated}
          cartQueryLoading={cartQueryLoading}
        />
      )}
    </>
  );
};

FirstLineupPreview.fragments = {
  anyCard: gql`
    fragment FirstLineupPreview_anyCard on AnyCardInterface {
      slug
      pictureUrl
      ...Card_anyCard
      ...EditFirstLineupDialog_anyCard
      livePrimaryOffer {
        id
        price {
          ...useMonetaryAmount_monetaryAmount
        }
      }
    }
    ${Card.fragments.anyCard}
    ${EditFirstLineupDialog.fragments.anyCard}
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<FirstLineupPreview_anyCard>,
  so5Leaderboard: gql`
    fragment FirstLineupPreview_so5Leaderboard on So5Leaderboard {
      slug
      ...EditFirstLineupDialog_so5Leaderboard
    }
    ${EditFirstLineupDialog.fragments.so5Leaderboard}
  ` as TypedDocumentNode<FirstLineupPreview_so5Leaderboard>,
};
